import { leyanbotClient, resolve } from 'services/api';
import type {
  Tag,
  SingleInstance,
  ProductData,
  PostMarkTagData,
  SpuTagsReturn,
  GetSpuOrderTagReturn,
  GetTagStatisticsData,
  GetTagStatisticsReturn,
  OverviewTableData,
  ImportRecord,
} from './type';

// https://app.apifox.com/link/project/625489/apis/api-152418650
export const getSpuClassTags = (spuClassId: number) => {
  return resolve<Tag[]>(
    leyanbotClient.get(`/oversea-knowledge/spu_knowledges/v3/spu_class/${spuClassId}/tags`),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-152426236
export const deleteTag = (tagId: number) => {
  return leyanbotClient.delete(`/oversea-knowledge/spu_knowledges/v3/tags/${tagId}`);
};

// https://app.apifox.com/link/project/625489/apis/api-152416004
export const postTag = (spuClassId: number, data: Omit<Tag, 'id' | 'subTags'>) => {
  return leyanbotClient.post(
    `/oversea-knowledge/spu_knowledges/v3/spu_class/${spuClassId}/tags`,
    data,
  );
};

// https://app.apifox.com/link/project/625489/apis/api-152421603
export const putTag = (data: Pick<Tag, 'id' | 'name' | 'multiLang' | 'description' | 'note'>) => {
  const { id, ...rest } = data;
  return leyanbotClient.put(`/oversea-knowledge/spu_knowledges/v3/tags/${id}`, rest);
};

// https://app.apifox.com/link/project/625489/apis/api-152364470
export const getSpuClass = () => {
  return resolve<SingleInstance[]>(
    leyanbotClient.get('/oversea-knowledge/spu_knowledges/v3/spu_class'),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-154853735
export const getSpuClassDetail = (spuClassId: number) => {
  return resolve<SingleInstance>(
    leyanbotClient.get(`/oversea-knowledge/spu_knowledges/v3/spu_class/${spuClassId}`),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-152365641
export const postProduct = (
  data: {
    spuClassId: number;
  } & ProductData,
) => {
  const { spuClassId, ...rest } = data;
  return leyanbotClient.post(
    `/oversea-knowledge/spu_knowledges/v3/spu_class/${spuClassId}/physical_products`,
    rest,
  );
};

// https://app.apifox.com/link/project/625489/apis/api-152423586
export const putProduct = (
  data: {
    physicalProductId: number;
  } & ProductData,
) => {
  const { physicalProductId, ...rest } = data;
  return leyanbotClient.put(
    `/oversea-knowledge/spu_knowledges/v3/physical_products/${physicalProductId}`,
    rest,
  );
};

// https://app.apifox.com/link/project/625489/apis/api-152426045
export const deleteProduct = (id: number) => {
  return leyanbotClient.delete(`/oversea-knowledge/spu_knowledges/v3/physical_products/${id}`);
};

// https://app.apifox.com/link/project/625489/apis/api-152828259
export const getSpuTags = (params: {
  itemInternalId?: string;
  sellerSku: string;
  spuId?: string;
  skuId?: string;
  itemKey?: string;
}) => {
  return resolve<SpuTagsReturn>(
    leyanbotClient.get('/oversea-knowledge/spu_knowledges/v3/spu_tags', {
      params,
    }),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-152657434
export const postMarkTag = (data: PostMarkTagData) => {
  return leyanbotClient.post('/oversea-knowledge/spu_knowledges/v3/mark_tag', data);
};

// https://app.apifox.com/link/project/625489/apis/api-152828746
export const getSpuOrderTag = (params: {
  itemKey: string;
  sellerSku: string;
  tradeId: string;
  reviewId?: string;
  skuId?: string;
  returnOrderId?: string;
}) => {
  return resolve<GetSpuOrderTagReturn[]>(
    leyanbotClient.get('/oversea-knowledge/spu_knowledges/v3/spu_order_tag', {
      params,
    }),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-154578991
export const deleteMarkTag = (markTagId: string) => {
  return leyanbotClient.delete(`/oversea-knowledge/spu_knowledges/v3/mark_tags/${markTagId}`);
};

// https://app.apifox.com/link/project/625489/apis/api-154579885
export const putMarkTag = (data: {
  markTagId: string;
  tagId: number;
  source: string;
  sourceId: string;
  itemCount: number;
  physicalProductId: number;
  physicalProductName: string;
  spuClassId: number;
  note: string;
}) => {
  const { markTagId } = data;
  return leyanbotClient.put(`/oversea-knowledge/spu_knowledges/v3/mark_tags/${markTagId}`, data);
};

// https://app.apifox.com/link/project/625489/apis/api-152773663
export const getTagStatistics = (data: GetTagStatisticsData) => {
  return resolve<GetTagStatisticsReturn>(
    leyanbotClient.post('/oversea-knowledge/spu_knowledges/v3/query_tag_statistics', data),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-152789208
export const getTagStatisticsPage = (
  data: GetTagStatisticsData & {
    tagLevel: number;
    page: {
      pageNo: number;
      pageSize: number;
    };
  },
) => {
  return leyanbotClient.post<{
    data: OverviewTableData[];
    page: {
      pageNo: number;
      pageSize: number;
      total: number;
    };
  }>('/oversea-knowledge/spu_knowledges/v3/page_query_tag_statistics', data);
};

// https://app.apifox.com/link/project/625489/apis/api-154856611
export const markTagExport = (
  data: GetTagStatisticsData & {
    tagLevel: number;
  },
) => {
  return resolve<{ resultUrl: string }>(
    leyanbotClient.post('/oversea-knowledge/spu_knowledges/v3/mark_tags/export', data),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-155854543
export const getAllTags = () => {
  return resolve<{ tags: Tag[] }[]>(
    leyanbotClient.get('/oversea-knowledge/spu_knowledges/v3/tags'),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-155610481
export const getImportRecord = () => {
  return resolve<ImportRecord[]>(
    leyanbotClient.get('/oversea-knowledge/spu_knowledges/v3/tag_product/import_history'),
  );
};

// https://app.apifox.com/link/project/625489/apis/api-152435097
export const importTag = (data: { fileUrl: string; isCheck: boolean }) => {
  return resolve<{
    successRowCount: number;
  }>(leyanbotClient.post('/oversea-knowledge/spu_knowledges/v3/tags/import', data));
};

// https://app.apifox.com/link/project/625489/apis/api-152437100
export const importProduct = (data: { fileUrl: string; isCheck: boolean }) => {
  return resolve<{
    successRowCount: number;
  }>(leyanbotClient.post('/oversea-knowledge/spu_knowledges/v3/physical_products/import', data));
};
